import { ReportRecord } from "types/ShiftTracker";
import { useTypeSelectors } from "./useTypeSelectors";
import { roleDa } from "mocks/roleDA";

const routeStatsOrder = [
  { name: "Standard Large", role: [0], size: 2 },
  { name: "Standard Cube", role: [0], size: 3 },
  { name: "Flex", role: [8], size: null },
  { name: "Cancelled", role: [7, 9], size: null },
  { name: "Rejected", role: [10], size: null },
  { name: "Other", role: [1, 2, 3, 4, 5, 6], size: null },
];

const routeStatsChartOrder = [
  { name: "Standard Large", role: [0], size: 2 },
  { name: "Standard Cube", role: [0], size: 3 },
  { name: "Standard Other", role: [0], size: [1, 4, 5, 6] },
  { name: "Flex", role: [8], size: null },
  { name: "Client Cancelled", role: [7], size: null },
  { name: "MYCU Cancelled", role: [9], size: null },
  { name: "Rejected", role: [10], size: null },
  { name: "No Show", role: [1], size: null },
  { name: "Standby", role: [3], size: null },
  { name: "Rescue", role: [2], size: null },
  { name: "Training", role: [4], size: null },
  
];

export const useShiftReportDetails = () => {
  const {
    currentReport,
    currentReportDate,
    currentReportDispatchers,
    currentReportSupportDispatchers,
    currentReportSupervisor,
    currentReportSpecialSupervisor,
    currentReportFleetCoordinator,
    currentReportPerformanceCoordinator,
    currentReportStation,
  } = useTypeSelectors((state) => state.dasBoard);

  const routesStats = routeStatsOrder.map((role) => {
    const count = currentReport.filter((record) =>
      !role.size
        ? role.role.includes(record.role)
        : role.role.includes(record.role) && role.size === record.van?.fleet_size.id,
    ).length;
    return { name: role.name, count };
  });

  const routesGraphData = roleDa
    .filter((role) => role.roleScheduleId !== 17)
    .map((role) => {
      const count = currentReport.filter((record) => record.role === role.value).length;
      return { name: role.name, count };
    });

  const reduceData = (field: keyof ReportRecord) =>
    currentReport.length
      ? currentReport.map((el) => Number(el?.[field])).reduce((prev, curr) => prev + curr)
      : 0;

  const utaResult = reduceData("uta");
  const utlResult = reduceData("utl");
  const nslResult = reduceData("nsl");
  const bcResult = reduceData("bc");
  const optFailureResult = reduceData("otp_failure");
  const missingResult = reduceData("missing");
  const damageResult = reduceData("damage");
  const fddResult = reduceData("fdd");
  const missortResult = reduceData("missort");
  const ocResult = reduceData("oc");
  const othersResult = reduceData("others");
  const numPackages = reduceData("number_of_packages");

  const totalReturns =
    utaResult +
    utlResult +
    nslResult +
    bcResult +
    optFailureResult +
    missingResult +
    damageResult +
    fddResult +
    missortResult +
    ocResult +
    othersResult;
  const percentReturns = Number(numPackages !== 0 ? (100 / numPackages) * totalReturns : 0);
  const percentDelivers = Number(100 - percentReturns);

  return {
    overview: [
      { label: "Date", value: currentReportDate },
      { label: "Station", value: currentReportStation.title },
      {
        label: "Dispatcher",
        value: currentReportDispatchers.length
          ? currentReportDispatchers
              .map((user) => `${user?.first_name} ${user?.last_name}`)
              .join(", ")
          : "None",
      },
      {
        label: "Support Dispatcher",
        value: currentReportSupportDispatchers.length
          ? currentReportSupportDispatchers
              .map((user) => `${user?.first_name} ${user?.last_name}`)
              .join(", ")
          : "None",
      },
      {
        label: "Supervisor",
        value: currentReportSupervisor
          ? `${currentReportSupervisor?.first_name} ${currentReportSupervisor?.last_name}`
          : "None",
      },
      {
        label: "Special Supervisor",
        value: currentReportSpecialSupervisor
          ? `${currentReportSpecialSupervisor?.first_name} ${currentReportSpecialSupervisor?.last_name}`
          : "None",
      },
      {
        label: "Fleet Coordinator",
        value: currentReportFleetCoordinator
          ? `${currentReportFleetCoordinator?.first_name} ${currentReportFleetCoordinator?.last_name}`
          : "None",
      },
      {
        label: "Performance Coordinator",
        value: currentReportPerformanceCoordinator
          ? `${currentReportPerformanceCoordinator?.first_name} ${currentReportPerformanceCoordinator?.last_name}`
          : "None",
      },
    ],
    routesData: routesStats,
    routesGraphData: routesGraphData,
    deliveries: numPackages,
    deliveriesPercentage: percentDelivers,
    returns: totalReturns,
    returnsPercentage: percentReturns,
    returnsGraphData: [
      { name: "UTA", count: utaResult },
      { name: "UTL", count: utlResult },
      { name: "NSL", count: nslResult },
      { name: "BC", count: bcResult },
      { name: "OTP Failure", count: optFailureResult },
      { name: "Missing", count: missingResult },
      { name: "Missort", count: missortResult },
      { name: "Damage", count: damageResult },
      { name: "FDD", count: fddResult },
      { name: "OC", count: ocResult },
      { name: "Others", count: othersResult },
    ],
  };
};
